import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";

// "Rebuild Website" button + confirm dialog, shared by the Blogs and Jobs
// pages. Fires the backend proxy (/api/build/website) instead of the raw
// Cloud Build webhook, polls build status, and stays disabled while a
// build is running so it can't be double-fired.

const POLL_MS = 20000;

const authHeaders = () => {
  const token = JSON.parse(localStorage.getItem("user") || "{}").token;
  return token ? { authorization: `Bearer ${token}` } : {};
};

const RebuildWebsiteButton = () => {
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [firing, setFiring] = useState(false);
  const toast = useToast();
  const wasRunning = useRef(false);

  const checkStatus = async () => {
    try {
      const { data } = await axios.get("/api/build/website/status", { headers: authHeaders() });
      setRunning(data.running);
      if (wasRunning.current && !data.running) {
        toast({
          title: data.lastStatus === "SUCCESS" ? "Website rebuild finished" : `Website rebuild ended: ${data.lastStatus}`,
          status: data.lastStatus === "SUCCESS" ? "success" : "error",
          duration: 8000,
          isClosable: true,
        });
      }
      wasRunning.current = data.running;
    } catch (error) {
      // Status is best-effort; leave the button usable.
    }
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRebuild = async () => {
    setFiring(true);
    try {
      await axios.post("/api/build/website", {}, { headers: authHeaders() });
      toast({
        title: "Rebuild started",
        description: "Takes about 5-8 minutes. The live site stays up and switches over when the build finishes.",
        status: "success",
        duration: 8000,
        isClosable: true,
      });
      setRunning(true);
      wasRunning.current = true;
    } catch (error) {
      const alreadyRunning = error?.response?.status === 409;
      toast({
        title: alreadyRunning ? "A rebuild is already running" : "Could not start rebuild",
        description: alreadyRunning ? "Wait for the current build to finish." : error?.response?.data?.message || error.message,
        status: alreadyRunning ? "warning" : "error",
        duration: 8000,
        isClosable: true,
      });
      if (alreadyRunning) {
        setRunning(true);
        wasRunning.current = true;
      }
    }
    setFiring(false);
    setConfirming(false);
  };

  return (
    <>
      {confirming && (
        <div className="fixed top-0 left-0 h-screen bg-black bg-opacity-10 w-screen flex justify-center items-center z-50">
          <div className="w-1/2 h-1/2 bg-white p-5 flex flex-col items-center justify-evenly">
            <h1 className="text-2xl font-bold">Rebuilding Website</h1>
            <p className="text-center">
              Rebuilding publishes the current CMS content and takes about 5-8 minutes. The live site stays up while it builds. Continue only if you are
              sure what you are doing.
            </p>
            <div className="flex gap-5 ">
              <button onClick={onRebuild} disabled={firing} className="px-6 py-1 bg-red-500 rounded-md text-white text-lg uppercase disabled:opacity-50">
                {firing ? "Starting..." : "Rebuild"}
              </button>
              <button onClick={() => setConfirming(false)} className="px-6 py-1 bg-black rounded-md text-white text-lg uppercase">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <button
        onClick={() => setConfirming(true)}
        disabled={running}
        title={running ? "A rebuild is currently running" : "Rebuild praan.io from current CMS content"}
        className={`px-6 py-1 rounded-md text-white text-lg uppercase ${running ? "bg-gray-400 cursor-not-allowed animate-pulse" : "bg-red-500"}`}
      >
        {running ? "Rebuild running..." : "Rebuild Website"}
      </button>
    </>
  );
};

export default RebuildWebsiteButton;
